import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  TreeSelect,
  Typography,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as regionService from '../services/regionService';

const { Title } = Typography;
const { Search } = Input;

const getNextLevel = (level: regionService.RegionLevel): regionService.RegionLevel | null => {
  if (level === 'province') return 'city';
  if (level === 'city') return 'district';
  return null;
};

const filterRegionTree = (regions: regionService.RegionItem[], keyword: string): regionService.RegionItem[] => {
  const trimmedKeyword = keyword.trim();
  if (!trimmedKeyword) {
    return regions;
  }

  const lowerKeyword = trimmedKeyword.toLowerCase();
  return regions
    .map((region) => {
      const children = filterRegionTree(region.children || [], trimmedKeyword);
      const matched = region.name.toLowerCase().includes(lowerKeyword) || region.code.toLowerCase().includes(lowerKeyword);
      if (matched || children.length > 0) {
        return {
          ...region,
          children,
        };
      }
      return null;
    })
    .filter(Boolean) as regionService.RegionItem[];
};

const buildTreeSelectOptions = (regions: regionService.RegionItem[]): any[] => {
  return regions.map((region) => ({
    title: `${region.name} (${region.code})`,
    value: region.id,
    key: region.id,
    children: buildTreeSelectOptions(region.children || []),
  }));
};

const RegionManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [regions, setRegions] = useState<regionService.RegionItem[]>([]);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | '1' | '0'>('all');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRegion, setEditingRegion] = useState<regionService.RegionItem | null>(null);
  const [form] = Form.useForm();
  const currentLevel = Form.useWatch('level', form) as regionService.RegionLevel | undefined;

  const loadRegions = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const res = await regionService.getRegionTree({
        refresh,
        status: statusFilter === 'all' ? undefined : Number(statusFilter),
      });
      setRegions(res.data || []);
    } catch {
      message.error('加载地区数据失败');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void loadRegions();
  }, [loadRegions]);

  const filteredRegions = useMemo(() => filterRegionTree(regions, keyword), [regions, keyword]);
  const parentTreeOptions = useMemo(() => buildTreeSelectOptions(regions), [regions]);

  const openCreateModal = (parent?: regionService.RegionItem) => {
    setEditingRegion(null);
    form.resetFields();

    if (!parent) {
      form.setFieldsValue({
        level: 'province',
        sort: 0,
        status: 1,
      });
    } else {
      const nextLevel = getNextLevel(parent.level);
      if (!nextLevel) {
        message.warning('区级地区下不能继续新增下级地区');
        return;
      }
      form.setFieldsValue({
        parentId: parent.id,
        level: nextLevel,
        sort: 0,
        status: 1,
      });
    }

    setModalVisible(true);
  };

  const openEditModal = (record: regionService.RegionItem) => {
    setEditingRegion(record);
    form.setFieldsValue({
      parentId: record.parentId || undefined,
      code: record.code,
      name: record.name,
      level: record.level,
      sort: record.sort,
      status: record.status,
    });
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingRegion(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        code: values.code?.trim(),
        name: values.name?.trim(),
        parentId: values.level === 'province' ? '' : values.parentId,
      };

      setSubmitLoading(true);
      if (editingRegion) {
        await regionService.updateRegion(editingRegion.id, payload);
        message.success('地区更新成功');
      } else {
        await regionService.createRegion(payload);
        message.success('地区创建成功');
      }
      handleCloseModal();
      loadRegions(true);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || '保存地区失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (record: regionService.RegionItem) => {
    try {
      await regionService.deleteRegion(record.id);
      message.success('地区删除成功');
      loadRegions(true);
    } catch (error: any) {
      message.error(error?.message || '删除地区失败');
    }
  };

  const columns: ColumnsType<regionService.RegionItem> = [
    {
      title: '地区名称',
      dataIndex: 'name',
      key: 'name',
      width: 260,
    },
    {
      title: '地区编码',
      dataIndex: 'code',
      key: 'code',
      width: 140,
    },
    {
      title: '层级',
      dataIndex: 'level',
      key: 'level',
      width: 90,
      render: (level: regionService.RegionLevel) => regionService.regionLevelLabelMap[level] || level,
    },
    {
      title: '排序',
      dataIndex: 'sort',
      key: 'sort',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: number) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (value: string) => value ? value.replace('T', ' ').slice(0, 19) : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => {
        const nextLevel = getNextLevel(record.level);
        return (
          <Space size={0} separator={<span style={{ color: '#d9d9d9' }}>|</span>}>
            {nextLevel && (
              <Button type="link" size="small" icon={<PlusOutlined />} style={{ padding: '0 4px' }} onClick={() => openCreateModal(record)}>
                新增下级
              </Button>
            )}
            <Button type="link" size="small" icon={<EditOutlined />} style={{ padding: '0 4px' }} onClick={() => openEditModal(record)}>
              编辑
            </Button>
            <Popconfirm title="确定删除该地区吗？" onConfirm={() => handleDelete(record)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />} style={{ padding: '0 4px' }}>
                删除
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>地区管理</Title>
              <div style={{ color: '#8c8c8c', marginTop: 8 }}>维护全国省、市、区公共地区数据</div>
            </div>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => loadRegions(true)}>
                刷新
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateModal()}>
                新增省级地区
              </Button>
            </Space>
          </div>

          <Space wrap>
            <Search
              placeholder="搜索地区名称/编码"
              allowClear
              style={{ width: 260 }}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onSearch={setKeyword}
            />
            <Select
              value={statusFilter}
              style={{ width: 140 }}
              onChange={setStatusFilter}
              options={[
                { label: '全部状态', value: 'all' },
                { label: '仅启用', value: '1' },
                { label: '仅禁用', value: '0' },
              ]}
            />
          </Space>

          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={filteredRegions}
            pagination={false}
            size="small"
            expandable={{ defaultExpandAllRows: true }}
            locale={{ emptyText: keyword ? '未匹配到地区数据' : '暂无地区数据' }}
          />
        </Space>
      </Card>

      <Modal
        title={editingRegion ? '编辑地区' : '新增地区'}
        open={modalVisible}
        onCancel={handleCloseModal}
        onOk={handleSubmit}
        confirmLoading={submitLoading}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ sort: 0, status: 1, level: 'province' }}>
          <Form.Item name="parentId" label="上级地区">
            <TreeSelect
              allowClear
              treeDefaultExpandAll
              placeholder={currentLevel === 'province' ? '省级地区无需选择上级' : '请选择上级地区'}
              treeData={parentTreeOptions}
              disabled={currentLevel === 'province'}
            />
          </Form.Item>

          <Form.Item name="level" label="地区层级" rules={[{ required: true, message: '请选择地区层级' }]}>
            <Select options={regionService.regionLevelOptions} />
          </Form.Item>

          <Form.Item name="code" label="地区编码" rules={[{ required: true, message: '请输入地区编码' }]}>
            <Input placeholder="请输入地区编码" disabled={!!editingRegion} />
          </Form.Item>

          <Form.Item name="name" label="地区名称" rules={[{ required: true, message: '请输入地区名称' }]}>
            <Input placeholder="请输入地区名称" />
          </Form.Item>

          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="sort" label="排序" style={{ width: 160 }}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="status" label="状态" style={{ width: 160 }}>
              <Select
                options={[
                  { label: '启用', value: 1 },
                  { label: '禁用', value: 0 },
                ]}
              />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default RegionManagement;
